import React, { useState } from 'react';
import PremiumPageLayout from '../components/PremiumPageLayout';
import { Phone, Mail, MapPin, Send, MessageCircle, Sparkles } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    currentSituation: '',
    interestedProgram: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const programs = [
    'IT Job Ready System Engineer',
    'Cloud and DevOps Job Ready',
    'AI Engineer',
    'Cloud Engineer',
    'Cybersecurity Specialist',
    'Not sure yet'
  ];

  const situations = [
    'Currently working (non-IT)',
    'Recent IT graduate',
    'Current IT student',
    'Career changer',
    'Unemployed',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Split name into first_name and last_name
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const response = await fetch(`${BACKEND_URL}/api/submit-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email: formData.email,
          phone: formData.phone || '',
          interest: formData.interestedProgram || 'General Inquiry',
          message: `${formData.currentSituation ? 'Current Situation: ' + formData.currentSituation + '\n\n' : ''}${formData.message}`,
          consent: formData.consent,
          page_url: window.location.href,
          utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
          referrer: document.referrer || ''
        })
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          currentSituation: '',
          interestedProgram: '',
          message: '',
          consent: false
        });
      } else {
        const errorData = await response.json();
        console.error('Form submission error:', errorData);
        alert('There was an error submitting the form. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting the form. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PremiumPageLayout>
      <div className="relative py-24 px-4 overflow-hidden">
        <div className="max-w-[1200px] mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full border border-[#2563EB]/30 bg-[#2563EB]/5">
              <MessageCircle size={16} className="text-[#2563EB]" />
              <span className="text-sm font-semibold text-[#2563EB]">Get In Touch</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
              Let's Start Your IT Career Journey
            </h1>
            <p className="text-xl text-[#C7D2FE] max-w-3xl mx-auto">
              Have questions? Want to discuss which program is right for you? Our team is here to help.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Contact Cards */}
            <div 
              className="p-6 rounded-[20px] border border-white/10 backdrop-blur-sm text-center"
              style={{ background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(6, 182, 212, 0.04) 100%)' }}
            >
              <div className="inline-flex p-3 rounded-full bg-[#2563EB]/10 mb-4">
                <Phone size={24} className="text-[#2563EB]" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Phone</h3>
              <a href="tel:+61468607994" className="text-[#C7D2FE] hover:text-white transition-colors">
                +61 468 607 994
              </a>
            </div>

            <div 
              className="p-6 rounded-[20px] border border-white/10 backdrop-blur-sm text-center"
              style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(37, 99, 235, 0.04) 100%)' }}
            >
              <div className="inline-flex p-3 rounded-full bg-[#6366F1]/10 mb-4">
                <Mail size={24} className="text-[#6366F1]" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Email</h3>
              <a href="mailto:info@careerlift.com.au" className="text-[#C7D2FE] hover:text-white transition-colors">
                info@careerlift.com.au
              </a>
            </div>

            <div 
              className="p-6 rounded-[20px] border border-white/10 backdrop-blur-sm text-center"
              style={{ background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(37, 99, 235, 0.04) 100%)' }}
            >
              <div className="inline-flex p-3 rounded-full bg-[#06B6D4]/10 mb-4">
                <MapPin size={24} className="text-[#06B6D4]" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Location</h3>
              <p className="text-[#C7D2FE]">Sydney, Australia</p>
            </div>
          </div>

          {/* Contact Form */}
          <div 
            className="rounded-[24px] p-8 md:p-12 border border-white/10 backdrop-blur-md"
            style={{ 
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(99, 102, 241, 0.04) 100%)',
              boxShadow: '0 22px 65px rgba(15, 23, 42, 0.40), inset 0 1px 0 0 rgba(255,255,255,0.1)'
            }}
          >
            {submitSuccess ? (
              <div className="text-center py-12">
                <div className="inline-flex p-4 rounded-full bg-[#16A34A]/10 mb-6">
                  <Sparkles size={48} className="text-[#16A34A]" strokeWidth={1.5} />
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">Thank You!</h2>
                <p className="text-lg text-[#C7D2FE] mb-8">
                  We've received your message and will get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setSubmitSuccess(false)}
                  className="px-8 py-4 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold transition-all"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB] transition-colors"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB] transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB] transition-colors"
                      placeholder="+61 4XX XXX XXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">Current Situation</label>
                    <select
                      name="currentSituation"
                      value={formData.currentSituation}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#2563EB] transition-colors"
                    >
                      <option value="" className="bg-[#0B1120]">Select...</option>
                      {situations.map((sit, idx) => (
                        <option key={idx} value={sit} className="bg-[#0B1120]">{sit}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Interested Program</label>
                  <select
                    name="interestedProgram"
                    value={formData.interestedProgram}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#2563EB] transition-colors"
                  >
                    <option value="" className="bg-[#0B1120]">Select a program...</option>
                    {programs.map((prog, idx) => (
                      <option key={idx} value={prog} className="bg-[#0B1120]">{prog}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB] transition-colors resize-none"
                    placeholder="Tell us about your goals and any questions you have..."
                  />
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="consent"
                    id="consent"
                    checked={formData.consent}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                  <label htmlFor="consent" className="text-sm text-[#C7D2FE]">
                    I agree to be contacted by CareerLift regarding my enquiry and understand my information will be handled according to the privacy policy.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold text-lg transition-all duration-300 hover:shadow-[0_20px_50px_rgba(37,99,235,0.4)] disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={20} />
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Decorative Glows */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#6366F1]/10 rounded-full blur-[120px] pointer-events-none"></div>
      </div>
    </PremiumPageLayout>
  );
};

export default ContactPage;
